import { useCallback, useEffect, useState } from "react";
import clsx from "clsx";
import { astralApi } from "../api/astral";
import { useGameStore } from "../store/useGameStore";
import { AstralChip, AstralDetailModal } from "../components/AstralDetailModal";
import { AstralAutoGatherOverlay } from "../components/AstralAutoGatherOverlay";
import { FormattedNumber } from "../components/FormattedNumber";
import { ModalBackdrop } from "../components/ModalBackdrop";
import { astralAutoGatherEntitlements } from "../utils/premium";
import type { AstralRarity, AstralShop, AstralShopItem, AstralState, PlayerAstral, QuickSellSettings } from "../types";

const QUICK_SELL_OPTIONS: { id: AstralRarity; label: string; color: string }[] = [
  { id: "gray", label: "Gray", color: "text-gray-400" },
  { id: "green", label: "Green", color: "text-green-400" },
  { id: "blue", label: "Blue", color: "text-blue-400" },
  { id: "purple", label: "Purple", color: "text-purple-400" },
];

const AUTO_SELL_RARITY_OPTIONS: { id: AstralRarity; label: string; color: string }[] = [
  ...QUICK_SELL_OPTIONS,
  { id: "orange", label: "Orange", color: "text-orange-400" },
  { id: "red", label: "Red", color: "text-red-400" },
];

type AstralTab = "gather" | "shop";

const SHOP_RARITIES: AstralRarity[] = ["purple", "orange", "red"];

const SHOP_RARITY_LABEL: Record<string, string> = {
  purple: "Purple",
  orange: "Orange",
  red: "Red",
};

export function AstralScreen() {
  const { hero, balance, setScreen, refreshBalance, refreshHero } = useGameStore();
  const [tab, setTab] = useState<AstralTab>("gather");
  const [state, setState] = useState<AstralState | null>(null);
  const [shop, setShop] = useState<AstralShop | null>(null);
  const [shopLoading, setShopLoading] = useState(false);
  const [shopConfirm, setShopConfirm] = useState<AstralShopItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [gathering, setGathering] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [selected, setSelected] = useState<PlayerAstral | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [lastGatheredId, setLastGatheredId] = useState<number | null>(null);
  const [showQuickSellConfirm, setShowQuickSellConfirm] = useState(false);
  const [sellConfirm, setSellConfirm] = useState<PlayerAstral | null>(null);
  const [quickSellDraft, setQuickSellDraft] = useState<QuickSellSettings>({
    rarities: ["gray", "green"],
    remember: false,
  });
  const [savingQuickSell, setSavingQuickSell] = useState(false);
  const [autoGoldBudget, setAutoGoldBudget] = useState("");
  const [autoSellRarities, setAutoSellRarities] = useState<AstralRarity[]>(["gray", "green"]);
  const [autoGatherOverlay, setAutoGatherOverlay] = useState(false);

  const autoGatherUnlock = astralAutoGatherEntitlements(hero?.premium ?? null).unlocked;

  const load = useCallback(async () => {
    setError(null);
    try {
      const data = await astralApi.getState();
      setState(data);
      if (data.quick_sell_settings) {
        setQuickSellDraft(data.quick_sell_settings);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load astrals");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const loadShop = useCallback(async () => {
    setShopLoading(true);
    setError(null);
    try {
      const data = await astralApi.getShop();
      setShop(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load astral shop");
    } finally {
      setShopLoading(false);
    }
  }, []);

  useEffect(() => {
    if (tab === "shop") {
      void loadShop();
    }
  }, [tab, loadShop]);

  const handlePurchase = async (item: AstralShopItem) => {
    setActionLoading(true);
    setError(null);
    setShopConfirm(null);
    try {
      const res = await astralApi.purchase(item.template_code);
      setMessage(res.message);
      await Promise.all([load(), loadShop(), refreshBalance(), refreshHero()]);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Purchase failed");
    } finally {
      setActionLoading(false);
    }
  };

  const gold = state?.gold ?? balance?.gold ?? 0;
  const unlocked = state?.unlocked ?? (hero?.level ?? 0) >= (state?.unlock_level ?? 25);
  const minGold = state?.min_gold_balance ?? 20000;
  const bagFull = (state?.inventory_used ?? 0) >= (state?.inventory_capacity ?? 16);
  const canGather = unlocked && gold >= minGold && !bagFull;
  const canAutoGather = unlocked && gold >= minGold && autoGatherUnlock;

  const handleGather = async (tier: number) => {
    if (!canGather || gathering) return;
    setGathering(true);
    setMessage(null);
    setError(null);
    try {
      const res = await astralApi.gather(tier);
      setState(res);
      await Promise.all([refreshBalance(), refreshHero()]);
      const gr = res.gather_result;
      if (gr.template_code === null) {
        setMessage("Nothing gathered — the chain reset to tier 1.");
        setLastGatheredId(null);
      } else {
        if (gr.gathered_astral) {
          setLastGatheredId(gr.gathered_astral.id);
          setTimeout(() => setLastGatheredId(null), 2000);
        }
        const parts = [`Gathered ${gr.gathered_astral?.name ?? "astral"}!`];
        if (gr.star_dust_gained > 0) parts.push(`+${gr.star_dust_gained} star dust`);
        if (gr.chain_reset) {
          parts.push("Chain reset to tier 1.");
        } else if (gr.chain_advanced) {
          parts.push("Chain advanced!");
        }
        setMessage(parts.join(" · "));
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Gather failed");
    } finally {
      setGathering(false);
    }
  };

  const handleAutoGather = async () => {
    if (!canAutoGather || gathering) return;
    const budget = Math.floor(Number(autoGoldBudget));
    if (!Number.isFinite(budget) || budget < 1) {
      setError("Enter a valid gold amount to spend");
      return;
    }
    if (budget > gold) {
      setError("Budget cannot exceed your current gold");
      return;
    }
    if (autoSellRarities.length === 0) {
      setError("Select at least one rarity to auto-sell when the bag is full");
      return;
    }
    setGathering(true);
    setAutoGatherOverlay(true);
    setMessage(null);
    setError(null);
    const started = Date.now();
    try {
      const res = await astralApi.autoGather(budget, autoSellRarities);
      const waitMs = Math.max(0, 10_000 - (Date.now() - started));
      if (waitMs > 0) {
        await new Promise((r) => setTimeout(r, waitMs));
      }
      setState(res);
      await Promise.all([refreshBalance(), refreshHero()]);
      const s = res.auto_gather_summary;
      const parts = [
        `Auto gather: ${s.gather_count} pulls · spent ${s.gold_spent.toLocaleString()} gold · +${s.astrals_gained} astral(s) · +${s.star_dust_gained} dust`,
      ];
      if (s.astrals_sold > 0) {
        parts.push(
          `sold ${s.astrals_sold} (${s.sell_rarities.join(", ")}) · +${s.sell_gold_gained.toLocaleString()} gold`,
        );
      }
      if (s.stopped_early_bag_full) {
        parts.push("stopped early — bag still full");
      }
      setMessage(parts.join(" · "));
    } catch (e) {
      const waitMs = Math.max(0, 10_000 - (Date.now() - started));
      if (waitMs > 0) {
        await new Promise((r) => setTimeout(r, waitMs));
      }
      setError(e instanceof Error ? e.message : "Auto gather failed");
    } finally {
      setAutoGatherOverlay(false);
      setGathering(false);
    }
  };

  const toggleAutoSellRarity = (rarity: AstralRarity) => {
    setAutoSellRarities((prev) => {
      const has = prev.includes(rarity);
      const next = has ? prev.filter((r) => r !== rarity) : [...prev, rarity];
      return next.length === 0 ? prev : next;
    });
  };

  const runAction = async (fn: () => Promise<AstralState>) => {
    setActionLoading(true);
    setError(null);
    try {
      const data = await fn();
      setState(data);
      await Promise.all([refreshBalance(), refreshHero()]);
      setSelected(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Action failed");
    } finally {
      setActionLoading(false);
    }
  };

  const persistQuickSellSettings = async (next: QuickSellSettings) => {
    setSavingQuickSell(true);
    setError(null);
    try {
      const data = await astralApi.updateQuickSellSettings(next);
      setState(data);
      setQuickSellDraft(data.quick_sell_settings);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to save quick sell settings");
    } finally {
      setSavingQuickSell(false);
    }
  };

  const toggleQuickSellRarity = (rarity: AstralRarity) => {
    const has = quickSellDraft.rarities.includes(rarity);
    const nextRarities = has
      ? quickSellDraft.rarities.filter((r) => r !== rarity)
      : [...quickSellDraft.rarities, rarity];
    if (nextRarities.length === 0) return;
    const next = { ...quickSellDraft, rarities: nextRarities };
    setQuickSellDraft(next);
    void persistQuickSellSettings(next);
  };

  const toggleQuickSellRemember = () => {
    const next = { ...quickSellDraft, remember: !quickSellDraft.remember };
    setQuickSellDraft(next);
    void persistQuickSellSettings(next);
  };

  const executeQuickSell = async () => {
    setActionLoading(true);
    setError(null);
    setShowQuickSellConfirm(false);
    try {
      const data = await astralApi.quickSell(quickSellDraft.rarities);
      setState(data);
      await refreshBalance();
      const qs = data.quick_sell;
      if (qs && qs.count > 0) {
        const parts = [
          `Sold ${qs.count} astral(s)`,
          `+${qs.gold.toLocaleString()} gold`,
        ];
        if (qs.star_dust > 0) parts.push(`+${qs.star_dust} star dust`);
        setMessage(parts.join(" · "));
      } else {
        setMessage("No matching astrals to sell.");
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Quick sell failed");
    } finally {
      setActionLoading(false);
    }
  };

  const handleQuickSellClick = () => {
    if (quickSellDraft.remember) {
      void executeQuickSell();
    } else {
      setShowQuickSellConfirm(true);
    }
  };

  const handleEquip = (astralId: number) => {
    const freeSlot = state?.equipped_slots.find((s) => !s.astral);
    if (!freeSlot) {
      setError("All equip slots are full — unequip an astral first");
      return;
    }
    return runAction(() => astralApi.equip(astralId, freeSlot.slot));
  };

  const executeSell = async (astral: PlayerAstral) => {
    setSellConfirm(null);
    await runAction(() => astralApi.sell(astral.id));
  };

  const hasFreeEquipSlot = state?.equipped_slots.some((s) => !s.astral) ?? false;

  if (!hero) {
    return <div className="p-4 text-gray-400 text-center py-12">Loading hero...</div>;
  }

  return (
    <div className="screen-root p-4 space-y-4 pb-28">
      <AstralAutoGatherOverlay active={autoGatherOverlay} />
      <div className="flex items-center gap-3">
        <button onClick={() => setScreen("main")} className="text-kingdom-glow text-sm font-medium">
          ← Back
        </button>
        <h2 className="text-xl font-bold font-game text-gold-400 flex-1 text-center">✦ Astrals</h2>
        <div className="w-12" />
      </div>

      <div className="flex rounded-xl border border-kingdom-mid bg-kingdom-dark/80 p-1">
        {(["gather", "shop"] as const).map((id) => (
          <button
            key={id}
            type="button"
            onClick={() => setTab(id)}
            className={clsx(
              "flex-1 py-2 rounded-lg text-sm font-semibold transition-colors",
              tab === id
                ? "bg-kingdom-mid text-gold-300 shadow-sm"
                : "text-gray-400 hover:text-gray-200",
            )}
          >
            {id === "gather" ? "Gather" : "Shop"}
          </button>
        ))}
      </div>

      {!unlocked && (
        <div className="rounded-xl border border-amber-600/50 bg-amber-900/20 p-4 text-center text-amber-200 text-sm">
          Unlocks at hero level {state?.unlock_level ?? 25}
        </div>
      )}

      {unlocked && tab === "gather" && gold < minGold && (
        <div className="rounded-xl border border-rose-600/50 bg-rose-900/20 p-3 text-center text-rose-200 text-xs">
          Need at least <FormattedNumber value={minGold} mode="exact" /> gold to gather (you have{" "}
          <FormattedNumber value={gold} mode="exact" />)
        </div>
      )}

      {unlocked && tab === "gather" && bagFull && (
        <div className="rounded-xl border border-amber-600/50 bg-amber-900/20 p-3 text-center text-amber-200 text-xs">
          All {state?.inventory_capacity ?? 16} slots full — sell or equip astrals to gather more
        </div>
      )}

      {error && (
        <div className="rounded-xl border border-rose-500/50 bg-rose-900/30 p-3 text-rose-200 text-sm">{error}</div>
      )}
      {message && (
        <div className="rounded-xl border border-sky-500/50 bg-sky-900/30 p-3 text-sky-200 text-sm">{message}</div>
      )}

      {tab === "gather" && loading ? (
        <div className="text-center text-gray-400 py-12">Loading astral field...</div>
      ) : tab === "gather" && state ? (
        <>
          <div className="flex items-center justify-between text-sm px-1 flex-wrap gap-y-1">
            <span className="text-gray-400">
              Value: <span className="text-gold-300 font-bold">{state.total_value}</span>
            </span>
            <span className="text-violet-300">
              ✦ <FormattedNumber value={state.star_dust} mode="exact" /> dust
            </span>
            <span className="text-gray-400">
              Slots:{" "}
              <span className="text-kingdom-glow font-bold">
                {state.inventory_used}/{state.inventory_capacity}
              </span>
            </span>
            <span className="text-gray-400">
              Chain: <span className="text-kingdom-glow font-bold">{state.unlocked_tier}</span>/5
            </span>
          </div>

          {/* Equipped astrals (separate from bag) */}
          {state.equip_slot_count > 0 && (
            <div className="rounded-xl border border-kingdom-mid bg-kingdom-dark/50 p-3">
              <div className="text-xs text-gray-400 mb-2">Equipped</div>
              <div className="flex flex-wrap gap-2 justify-center">
                {state.equipped_slots.map(({ slot, astral }) => (
                  <div key={slot} className="flex flex-col items-center gap-0.5">
                    {astral ? (
                      <AstralChip
                        astral={astral}
                        onClick={() => setSelected(astral)}
                        size="sm"
                        labeled
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-full border border-dashed border-emerald-700/50 flex items-center justify-center text-emerald-700/60 text-[10px]">
                        E{slot + 1}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 16 inventory slots */}
          <div className="relative rounded-2xl border border-indigo-500/40 bg-gradient-to-b from-indigo-950 via-kingdom-dark to-kingdom-purple p-3 overflow-hidden">
            <div className="absolute inset-0 opacity-30 pointer-events-none">
              {Array.from({ length: 24 }).map((_, i) => (
                <span
                  key={i}
                  className="absolute text-white/40 text-[8px]"
                  style={{
                    left: `${(i * 17 + 7) % 100}%`,
                    top: `${(i * 23 + 11) % 100}%`,
                  }}
                >
                  ✦
                </span>
              ))}
            </div>

            <div className="relative z-10 grid grid-cols-4 gap-2">
              {state.inventory_slots.map(({ slot, astral }) => (
                <div
                  key={slot}
                  className={clsx(
                    "min-h-[5.25rem] rounded-xl border flex items-center justify-center transition-all p-1",
                    astral
                      ? lastGatheredId === astral.id
                        ? "border-gold-400 bg-gold-500/20 animate-pulse"
                        : "border-kingdom-light/40 bg-kingdom-dark/60"
                      : "border-dashed border-gray-700 bg-black/20",
                  )}
                >
                  {astral ? (
                    <AstralChip astral={astral} onClick={() => setSelected(astral)} size="sm" labeled />
                  ) : (
                    <span className="text-gray-700 text-xs">{slot + 1}</span>
                  )}
                </div>
              ))}
            </div>
          </div>

          {autoGatherUnlock && (
            <div className="rounded-xl border border-violet-500/40 bg-violet-950/30 p-3 space-y-2">
              <div className="text-sm font-bold text-violet-200">Auto gather (VIP L4+ or Pro)</div>
              <p className="text-[11px] text-gray-400">
                Set a gold budget and which rarities to sell from the bag when slots run out. Gathering stops early
                if the bag stays full after auto-sell.
              </p>
              <div className="text-[11px] text-gray-500 font-semibold">Auto-sell these rarities (bag only)</div>
              <div className="flex flex-wrap gap-2">
                {AUTO_SELL_RARITY_OPTIONS.map(({ id, label, color }) => {
                  const on = autoSellRarities.includes(id);
                  return (
                    <button
                      key={id}
                      type="button"
                      onClick={() => toggleAutoSellRarity(id)}
                      disabled={gathering}
                      className={clsx(
                        "px-2.5 py-1 rounded-lg text-xs font-bold border transition-colors",
                        on
                          ? "border-violet-500/70 bg-violet-900/50 text-white"
                          : "border-gray-700 bg-black/20 text-gray-500",
                        color,
                      )}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
              <div className="flex gap-2">
                <input
                  type="number"
                  min={1}
                  max={gold}
                  value={autoGoldBudget}
                  onChange={(e) => setAutoGoldBudget(e.target.value)}
                  placeholder="Gold budget"
                  className="flex-1 rounded-lg border border-kingdom-mid bg-black/30 px-3 py-2 text-sm text-white"
                  disabled={!canAutoGather || gathering}
                />
                <button
                  type="button"
                  disabled={
                    !canAutoGather || gathering || !autoGoldBudget.trim() || autoSellRarities.length === 0
                  }
                  onClick={() => void handleAutoGather()}
                  className="shrink-0 px-4 py-2 rounded-lg bg-violet-600 text-white text-sm font-bold disabled:opacity-40"
                >
                  Auto
                </button>
              </div>
            </div>
          )}

          {/* Tier buttons */}
          <div className="grid grid-cols-5 gap-2">
            {state.tiers.map((t) => {
              const lit = t.tier <= state.unlocked_tier;
              const disabled = !canGather || !lit || gathering;
              return (
                <button
                  key={t.tier}
                  type="button"
                  disabled={disabled}
                  onClick={() => void handleGather(t.tier)}
                  className={clsx(
                    "rounded-xl border py-3 px-1 text-center transition-all",
                    lit
                      ? "border-gold-500/70 bg-gradient-to-b from-indigo-800/80 to-kingdom-dark shadow-[0_0_12px_rgba(251,191,36,0.25)]"
                      : "border-gray-700 bg-gray-900/50 opacity-40",
                    disabled && "opacity-50 cursor-not-allowed",
                    !disabled && lit && "hover:brightness-110 active:scale-95",
                  )}
                >
                  <div className="text-[10px] text-gray-400 leading-tight">{t.name.split(" ")[0]}</div>
                  <div className="text-lg font-bold text-gold-300">{t.tier}</div>
                  <div className="text-[9px] text-gray-500">
                    <FormattedNumber value={t.cost} mode="compact" />
                  </div>
                </button>
              );
            })}
          </div>

          {/* Quick sell */}
          <div className="rounded-xl border border-amber-600/40 bg-amber-900/20 p-3 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-bold text-amber-200">Quick sell</span>
              {savingQuickSell && <span className="text-[10px] text-gray-500">Saving…</span>}
            </div>
            <div className="flex flex-wrap gap-2">
              {QUICK_SELL_OPTIONS.map(({ id, label, color }) => {
                const on = quickSellDraft.rarities.includes(id);
                return (
                  <button
                    key={id}
                    type="button"
                    onClick={() => toggleQuickSellRarity(id)}
                    className={clsx(
                      "px-3 py-1.5 rounded-lg text-xs font-bold border transition-colors",
                      on
                        ? "border-amber-500/70 bg-amber-800/40 text-white"
                        : "border-gray-700 bg-black/20 text-gray-500",
                      color,
                    )}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
            <label className="flex items-center gap-2 text-xs text-gray-400 cursor-pointer">
              <input
                type="checkbox"
                checked={quickSellDraft.remember}
                onChange={toggleQuickSellRemember}
                className="rounded border-gray-600"
              />
              Don&apos;t ask every time — sell immediately with these rarities
            </label>
            <button
              type="button"
              disabled={actionLoading || quickSellDraft.rarities.length === 0}
              onClick={() => void handleQuickSellClick()}
              className="w-full py-2.5 rounded-xl border border-amber-500/50 bg-amber-800/40 text-amber-100 text-sm font-bold disabled:opacity-50"
            >
              Quick sell selected
            </button>
          </div>
        </>
      ) : tab === "shop" ? (
        shopLoading ? (
          <div className="text-center text-gray-400 py-12">Loading astral shop...</div>
        ) : shop ? (
          <>
            <div className="flex items-center justify-between text-sm px-1 flex-wrap gap-y-1">
              <span className="text-violet-300">
                ✦ <FormattedNumber value={shop.star_dust} mode="exact" /> dust
              </span>
              <span className="text-gray-400">
                Slots:{" "}
                <span className="text-kingdom-glow font-bold">
                  {shop.inventory_used}/{shop.inventory_capacity}
                </span>
              </span>
            </div>

            {!shop.unlocked && (
              <div className="rounded-xl border border-amber-600/50 bg-amber-900/20 p-4 text-center text-amber-200 text-sm">
                Shop unlocks at hero level {shop.unlock_level}
              </div>
            )}

            {shop.unlocked && shop.inventory_used >= shop.inventory_capacity && (
              <div className="rounded-xl border border-amber-600/50 bg-amber-900/20 p-3 text-center text-amber-200 text-xs">
                Bag full — sell or equip an astral before buying
              </div>
            )}

            {SHOP_RARITIES.map((rarity) => {
              const items = shop.items.filter((i) => i.rarity === rarity);
              if (items.length === 0) return null;
              const price = shop.prices_by_rarity[rarity];
              return (
                <section
                  key={rarity}
                  className="rounded-xl border border-kingdom-mid bg-kingdom-purple/60 p-3 space-y-2"
                >
                  <div className="flex items-center justify-between">
                    <h3
                      className={clsx(
                        "text-sm font-bold capitalize",
                        rarity === "purple" && "text-purple-400",
                        rarity === "orange" && "text-orange-400",
                        rarity === "red" && "text-red-400",
                      )}
                    >
                      {SHOP_RARITY_LABEL[rarity]}
                    </h3>
                    <span className="text-xs text-violet-300">
                      <FormattedNumber value={price} mode="exact" /> dust each
                    </span>
                  </div>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {items.map((item) => {
                      const canBuy =
                        shop.unlocked &&
                        shop.inventory_used < shop.inventory_capacity &&
                        shop.star_dust >= item.star_dust_cost;
                      return (
                        <div
                          key={item.template_code}
                          className="flex items-start gap-2 rounded-lg border border-kingdom-mid/80 bg-kingdom-dark/60 p-2"
                        >
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold text-white text-sm truncate">{item.name}</div>
                            <div className="text-[10px] text-gray-400 line-clamp-2">{item.description}</div>
                          </div>
                          <button
                            type="button"
                            disabled={!canBuy || actionLoading}
                            onClick={() => setShopConfirm(item)}
                            className={clsx(
                              "shrink-0 px-3 py-1.5 rounded-lg text-xs font-bold border transition-colors",
                              canBuy
                                ? "border-violet-500/60 bg-violet-900/40 text-violet-100 hover:bg-violet-800/50"
                                : "border-gray-700 text-gray-500 cursor-not-allowed opacity-60",
                            )}
                          >
                            Buy
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </section>
              );
            })}
          </>
        ) : null
      ) : null}

      {shopConfirm && (
        <ModalBackdrop onClose={() => setShopConfirm(null)} className="z-50 items-center bg-black/70">
          <div className="w-full max-w-sm bg-kingdom-purple border border-violet-500/50 rounded-2xl p-4 space-y-4">
            <h3 className="font-bold text-white">Buy {shopConfirm.name}?</h3>
            <p className="text-sm text-gray-300">{shopConfirm.description}</p>
            <p className="text-sm text-violet-300">
              Cost: <FormattedNumber value={shopConfirm.star_dust_cost} mode="exact" /> star dust
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setShopConfirm(null)}
                className="flex-1 py-2.5 rounded-xl border border-gray-600 text-gray-300 text-sm"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={actionLoading}
                onClick={() => void handlePurchase(shopConfirm)}
                className="flex-1 py-2.5 rounded-xl bg-violet-700 text-white text-sm font-bold disabled:opacity-50"
              >
                Buy
              </button>
            </div>
          </div>
        </ModalBackdrop>
      )}

      {sellConfirm && (
        <ModalBackdrop onClose={() => setSellConfirm(null)} className="z-[60] items-center bg-black/70">
          <div className="w-full max-w-sm bg-kingdom-purple border border-amber-600/50 rounded-2xl p-4 space-y-4">
            <h3 className="font-bold text-white">Sell {sellConfirm.name}?</h3>
            <p className="text-sm text-gray-300">
              You will receive{" "}
              <span className="text-gold-300">
                <FormattedNumber value={sellConfirm.sell_gold} mode="exact" /> gold
              </span>
              {sellConfirm.sell_star_dust > 0 && (
                <>
                  {" "}
                  and{" "}
                  <span className="text-violet-300">
                    <FormattedNumber value={sellConfirm.sell_star_dust} mode="exact" /> star dust
                  </span>
                </>
              )}
              . This cannot be undone.
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setSellConfirm(null)}
                className="flex-1 py-2.5 rounded-xl border border-gray-600 text-gray-300 text-sm"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={actionLoading}
                onClick={() => void executeSell(sellConfirm)}
                className="flex-1 py-2.5 rounded-xl bg-amber-700 text-white text-sm font-bold disabled:opacity-50"
              >
                Sell
              </button>
            </div>
          </div>
        </ModalBackdrop>
      )}

      {showQuickSellConfirm && (
        <ModalBackdrop onClose={() => setShowQuickSellConfirm(false)} className="z-50 items-center bg-black/70">
          <div className="w-full max-w-sm bg-kingdom-purple border border-amber-600/50 rounded-2xl p-4 space-y-4">
            <h3 className="font-bold text-white">Confirm quick sell</h3>
            <p className="text-sm text-gray-300">
              Sell all{" "}
              <span className="text-amber-300 capitalize">{quickSellDraft.rarities.join(", ")}</span>{" "}
              astrals in your bag?
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setShowQuickSellConfirm(false)}
                className="flex-1 py-2.5 rounded-xl border border-gray-600 text-gray-300 text-sm"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={actionLoading}
                onClick={() => void executeQuickSell()}
                className="flex-1 py-2.5 rounded-xl bg-amber-700 text-white text-sm font-bold disabled:opacity-50"
              >
                Sell
              </button>
            </div>
          </div>
        </ModalBackdrop>
      )}

      {selected && !sellConfirm && (
        <AstralDetailModal
          astral={selected}
          equipSlotCount={state?.equip_slot_count ?? 0}
          actionLoading={actionLoading}
          onClose={() => setSelected(null)}
          onSell={
            selected.equipped_slot === null ? () => setSellConfirm(selected) : undefined
          }
          onEquip={
            selected.equippable && selected.equipped_slot === null && hasFreeEquipSlot
              ? () => handleEquip(selected.id)
              : undefined
          }
          onUnequip={
            selected.equipped_slot !== null
              ? () => runAction(() => astralApi.unequip(selected.equipped_slot!))
              : undefined
          }
          onUpgrade={
            selected.can_upgrade ? () => runAction(() => astralApi.upgrade(selected.id)) : undefined
          }
        />
      )}
    </div>
  );
}
